import type {
  CompanyProfile,
  CompanyProfilesListResponse,
  CreateCompanyProfileRequest,
  PatchCompanyProfileRequest,
  BuildCompanyProfileRequest,
  PersonaDto,
  PersonasListResponse,
} from '@logspot-platform/platform-types';
import { ActionRunsService } from './action-runs-service';
import { ApiError, api } from './api-client';

type SuccessEnvelope<T> = { data: T };

export type {
  CompanyProfile,
  PersonaDto,
} from '@logspot-platform/platform-types';

export type ProfileBuildResult = {
  profile: CompanyProfile;
  sources: Array<{ claim: string; url: string }>;
  model_id: string;
};

export type ProfileBuildRunResult = {
  profile: CompanyProfile;
  sources: ProfileBuildResult['sources'];
  modelId: string;
};

// Org-scoped Company Profiles + the maintained persona taxonomy. Mirrors the
// backend admin endpoints (api/admin/company-profiles.ts, personas.ts).
export const CompanyProfilesService = {
  list: async (): Promise<CompanyProfilesListResponse> => {
    const r = await api.get<SuccessEnvelope<CompanyProfilesListResponse>>(
      '/admin/company-profiles',
    );
    return r.data;
  },

  get: async (id: string): Promise<CompanyProfile> => {
    const r = await api.get<SuccessEnvelope<{ profile: CompanyProfile }>>(
      `/admin/company-profiles/${id}`,
    );
    return r.data.profile;
  },

  create: async (
    input: CreateCompanyProfileRequest,
  ): Promise<CompanyProfile> => {
    const r = await api.post<SuccessEnvelope<{ profile: CompanyProfile }>>(
      '/admin/company-profiles',
      input,
    );
    return r.data.profile;
  },

  update: async (
    id: string,
    input: PatchCompanyProfileRequest,
  ): Promise<CompanyProfile> => {
    const r = await api.put<SuccessEnvelope<{ profile: CompanyProfile }>>(
      `/admin/company-profiles/${id}`,
      input,
    );
    return r.data.profile;
  },

  setDefault: async (id: string): Promise<void> => {
    await api.post(`/admin/company-profiles/${id}/default`, {});
  },

  // Starts the build without waiting; onboarding follows the run from Getting Started.
  startBuild: async (input: BuildCompanyProfileRequest): Promise<string> => {
    const r = await api.post<SuccessEnvelope<{ run_id: string }>>(
      '/admin/company-profiles/build',
      input,
    );
    return r.data.run_id;
  },

  // Starts the Profile Agent (spends profile.build) as a background run, then
  // waits for it and returns the built profile.
  build: async (
    input: BuildCompanyProfileRequest,
  ): Promise<ProfileBuildResult> => {
    const r = await api.post<SuccessEnvelope<{ run_id: string }>>(
      '/admin/company-profiles/build',
      input,
    );
    const run = await ActionRunsService.waitFor(
      r.data.run_id,
      'The profile build is still running.',
    );
    const result =
      run.result && typeof run.result === 'object'
        ? (run.result as Record<string, unknown>)
        : {};
    if (run.status !== 'succeeded' || typeof result.profile_id !== 'string') {
      throw new ApiError(500, run.error ?? 'The profile build failed.');
    }
    const profile = await CompanyProfilesService.get(result.profile_id);
    const sources = Array.isArray(result.sources)
      ? (result.sources as ProfileBuildResult['sources'])
      : [];
    return { profile, sources, model_id: run.model_id ?? '' };
  },

  // DELETE /admin/company-profiles/:id — persona links cascade; saved actions
  // pinned to it fall back to the org default.
  remove: async (id: string): Promise<void> => {
    await api.delete(`/admin/company-profiles/${id}`);
  },

  listPersonas: async (): Promise<PersonaDto[]> => {
    const r =
      await api.get<SuccessEnvelope<PersonasListResponse>>('/admin/personas');
    return r.data.personas;
  },
};
